const GA4_ID = process.env.GATSBY_GA4_ID
const GTM_ID = process.env.GATSBY_GTM_ID
const mappedNames = { login_success: 'login', signup_success: 'sign_up', purchase_success: 'purchase', delivery_details_completed: 'add_shipping_info', payment_started: 'add_payment_info' }
const ecommerceEvents = new Set(['select_item', 'view_item', 'view_cart', 'add_to_cart', 'remove_from_cart', 'begin_checkout', 'add_shipping_info', 'add_payment_info', 'purchase'])
const supportedEvents = new Set([...ecommerceEvents, 'page_view', 'search', 'login', 'sign_up', 'payment_rejected'])
const sentPurchases = new Set()
const number = value => value !== null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : undefined
const money = value => Math.round(value * 100) / 100

// Explicit allowlist: never forward customer, address, card or arbitrary API fields.
export function gaItems(products = [], quantity) {
  return products.filter(Boolean).map(product => {
    const id = product.item_id ?? product.sku ?? product.id ?? product.product_id
    const price = number(product.price ?? product.precio_unitario ?? product.precio)
    return {
      ...(id != null ? { item_id: String(id) } : {}),
      ...(product.item_name || product.descripcion ? { item_name: String(product.item_name || product.descripcion).slice(0, 100) } : {}),
      ...(product.item_category || product.id_categoria_app ? { item_category: String(product.item_category || product.id_categoria_app) } : {}),
      ...(product.affiliation || product.nombre_tienda ? { affiliation: String(product.affiliation || product.nombre_tienda) } : {}),
      ...(price !== undefined ? { price } : {}),
      quantity: Math.max(1, number(quantity ?? product.quantity ?? product.cantidad) || 1)
    }
  }).filter(item => item.item_id || item.item_name)
}

export function cartAnalytics(cart) {
  if (!cart) return { items: [] }
  return {
    items: gaItems(cart.productos_carrito || []),
    value: Math.max(0, Number(cart.total_carrito || 0) - Number(cart.cupon?.codigo_cupon ? cart.cupon.monto_descuento || 0 : 0)),
    shipping: Number(cart.info_carrito?.precio_envio || 0),
    coupon: cart.cupon?.codigo_cupon || undefined
  }
}

export function googleEvent(eventName, fields = {}) {
  const name = mappedNames[eventName] || eventName
  if (!supportedEvents.has(name)) return null
  if (name === 'page_view') return { event: name, page_path: fields.page_path, page_location: fields.page_location, page_referrer: fields.page_referrer, page_title: fields.page_title }
  if (name === 'search') return { event: name, search_term: fields.search_term }
  if (name === 'login' || name === 'sign_up') return { event: name, method: fields.properties?.method || 'website' }
  if (name === 'payment_rejected') return { event: name, payment_type: fields.payment_method, rejection_reason: fields.rejection_reason }
  const items = gaItems(fields.items || (fields.product_id ? [{ item_id: fields.product_id, price: fields.quantity ? Number(fields.revenue) / fields.quantity : fields.revenue, quantity: fields.quantity }] : []))
  if (!items.length) return null // Do not send incomplete ecommerce events.
  const transactionId = fields.order_id == null ? '' : String(fields.order_id)
  if (name === 'purchase' && !transactionId) return null
  const calculatedValue = items.every(item => item.price !== undefined) ? money(items.reduce((sum, item) => sum + item.price * item.quantity, 0)) : undefined
  return { event: name, ecommerce: {
    currency: fields.currency || 'PEN',
    ...(number(fields.value) !== undefined ? { value: money(Number(fields.value)) } : calculatedValue !== undefined ? { value: calculatedValue } : {}),
    items,
    ...(fields.coupon ? { coupon: String(fields.coupon) } : {}),
    ...(name === 'purchase' ? { transaction_id: transactionId, ...(number(fields.shipping) !== undefined ? { shipping: Number(fields.shipping) } : {}) } : {}),
    ...(name === 'add_payment_info' ? { payment_type: fields.payment_method } : {}),
    ...(name === 'add_shipping_info' ? { shipping_tier: fields.properties?.delivery_type } : {})
  } }
}

export function trackGoogle(eventName, fields) {
  if (typeof window === 'undefined') return
  const event = googleEvent(eventName, fields)
  if (!event) return
  const purchaseId = event.ecommerce?.transaction_id
  const purchaseKey = purchaseId ? `mdf_ga_purchase_${GA4_ID || GTM_ID || 'legacy'}_${purchaseId}` : null
  if (purchaseId) {
    if (sentPurchases.has(purchaseId)) return
    try { if (window.localStorage.getItem(purchaseKey)) return } catch {}
  }
  let sent = false
  if (GA4_ID) {
    window.dataLayer = window.dataLayer || []
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }
    const { event: name, ecommerce, ...parameters } = event
    window.gtag('event', name, { ...(ecommerce || parameters), send_to: GA4_ID })
    sent = true
  }
  if (GTM_ID) {
    window.dataLayer = window.dataLayer || []
    // Clear previous ecommerce and optional parameters before each event.
    window.dataLayer.push({ ecommerce: null, search_term: null, method: null, payment_type: null, rejection_reason: null })
    window.dataLayer.push({ ...event, ga4_direct: Boolean(GA4_ID) })
    sent = true
  } else if (!GA4_ID && typeof window.gtag === 'function' && event.event !== 'page_view') {
    const { event: name, ecommerce, ...parameters } = event
    window.gtag('event', name, ecommerce || parameters)
    sent = true
  }
  if (!sent) return
  if (purchaseId) {
    sentPurchases.add(purchaseId)
    try { window.localStorage.setItem(purchaseKey, '1') } catch {}
  }
}
